import { useEffect, useRef, useState } from "react";
import { frameSandbox, frameUrl } from "../../lib/frameUrl.js";
import type { CanvasState } from "../../lib/viewModel.js";
import { usePreviewFrame } from "../../lib/usePreviewFrame.js";
import { useSiteReady } from "../../lib/useSiteReady.js";
import { HANDOFF_COPY, backendHandoffPath } from "../../lib/xanoHandoff.js";

/**
 * The left pane: the thing being made.
 *
 * For most of a build there is nothing to preview, so the empty state is
 * designed rather than deferred to a spinner — the current action at display
 * scale over a quiet surface. That is what the user watches for several
 * minutes, and it is the pane's primary state, not a placeholder for one.
 *
 * There used to be a file list pinned under it. It listed paths the user has no
 * way to open and cannot act on, and it took the bottom third of the pane away
 * from the one thing they came for.
 */
export function BuildCanvas({
  canvas,
  currentAction,
  sessionId,
  hasBackend,
  onRestart,
}: {
  canvas: CanvasState;
  currentAction: string;
  sessionId: string;
  /** Whether this build has a Xano backend worth offering. See lib/xanoHandoff. */
  hasBackend: boolean;
  onRestart: () => void;
}) {
  return (
    // Scrolls internally rather than clipping when the viewport is short.
    <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto p-4 sm:p-6">
      {canvas.state === "nest" && <Nest currentAction={currentAction} />}
      {canvas.state === "framing" && (
        <Preview
          siteUrl={canvas.siteUrl}
          sessionId={sessionId}
          hasBackend={hasBackend}
          onRestart={onRestart}
        />
      )}
      {canvas.state === "closed" && <Closed canvas={canvas} onRestart={onRestart} />}
    </div>
  );
}

function Nest({ currentAction }: { currentAction: string }) {
  return (
    <div className="flex shrink-0 flex-col items-center justify-center rounded-2xl border border-dashed border-rule bg-surface/60 px-6 py-10 text-center sm:px-8 sm:py-16">
      <span
        aria-hidden
        className="mb-4 size-2.5 rounded-full bg-accent animate-pulse-dot sm:mb-6"
      />
      <p className="font-display text-xl leading-snug text-ink sm:text-3xl">{currentAction}</p>
      <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink-faint sm:mt-4">
        Your site will appear here when it goes live. Builds usually take a few minutes.
      </p>
    </div>
  );
}

/**
 * The gap between "deployed" and "reachable".
 *
 * Shown instead of the frame, not on top of it: a frame mounted against a host
 * that does not resolve yet stays broken even after DNS catches up, because the
 * browser holds onto the failure. Waiting to mount is the fix; this is what the
 * user looks at while that happens.
 */
function ComingOnline({ waitedMs }: { waitedMs: number }) {
  // The first few seconds are the normal case and need no explanation. Past
  // that the wait is unusual, and silence starts to read as a hang.
  const slow = waitedMs > 20_000;

  return (
    <div
      className="flex h-full flex-col items-center justify-center px-8 text-center"
      role="status"
      aria-live="polite"
    >
      <span aria-hidden className="mb-4 size-2.5 rounded-full bg-accent animate-pulse-dot" />
      <p className="text-sm text-ink-soft">Waiting for your address to come online…</p>
      <p className="mt-2 max-w-xs text-xs leading-relaxed text-ink-faint">
        {slow
          ? "This one is taking longer than usual. The link above will work as soon as it resolves."
          : "A new address takes a few seconds to start answering."}
      </p>
    </div>
  );
}

/**
 * How long to wait for a frame that never loads at all.
 *
 * This does NOT catch a refused frame. Verified in Chromium: a site that
 * blocks framing, or a host that does not resolve, still fires `load` for the
 * browser's own error page — so the timeout is already cancelled by the time
 * the frame is visibly empty. The content is cross-origin, so there is no way
 * to inspect it and find out.
 *
 * That is why nothing here depends on knowing whether the preview rendered.
 * The URL sits above the frame in every state, and a permanent escape hatch
 * sits below it. The frame is an enhancement; the link is the deliverable.
 */
const FRAME_TIMEOUT_MS = 6000;

function Preview({
  siteUrl,
  sessionId,
  hasBackend,
  onRestart,
}: {
  siteUrl: string;
  sessionId: string;
  hasBackend: boolean;
  onRestart: () => void;
}) {
  const [framed, setFramed] = useState<"waiting" | "loaded" | "unavailable">("waiting");
  const [copied, setCopied] = useState(false);
  // The key changes only on an explicit reload, and the refresh that makes that
  // reload work lives with it — see lib/usePreviewFrame.
  const { loadKey, reloading, reload } = usePreviewFrame(siteUrl);
  const frameRef = useRef<HTMLIFrameElement>(null);
  // The deploy reports a URL a few seconds before the edge routes it. Mounting
  // the frame on that URL is the race the user saw as "reload until it works" —
  // and a frame that mounted too early stays broken, because the browser caches
  // the failed lookup. So the frame is not mounted until the API has actually
  // fetched the site.
  const site = useSiteReady(sessionId, siteUrl);

  // Native listeners rather than React's synthetic ones: `load` and `error` do
  // not bubble, and React's synthetic coverage for them on an iframe is not
  // dependable. The timeout below is still the primary signal — a blocked frame
  // usually fires nothing at all — but a real error event should not be missed.
  const checking = site.status === "checking";

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    // Giving up is sticky: a browser that blocks the frame may still fire load
    // for its own blocked page, and treating that as success would swap a
    // working link back for an unusable frame.
    const onLoad = () => setFramed((s) => (s === "waiting" ? "loaded" : s));
    const onError = () => setFramed("unavailable");
    frame.addEventListener("load", onLoad);
    frame.addEventListener("error", onError);
    return () => {
      frame.removeEventListener("load", onLoad);
      frame.removeEventListener("error", onError);
    };
  }, [framed, checking]);

  // The clock starts when the frame mounts, not when the pane does. Started
  // earlier it would run down during the readiness poll and declare the frame
  // unavailable before it had been given a URL to load.
  useEffect(() => {
    if (checking || framed !== "waiting") return;
    const timer = setTimeout(() => setFramed("unavailable"), FRAME_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [framed, checking]);

  /**
   * Re-fetch the framed site, past the browser's copy of it.
   *
   * `framed` goes back to waiting so a frame that had been written off gets
   * another chance — the usual reason to press this is that the site was mid
   * deploy the first time. The refresh-then-re-point half is `usePreviewFrame`'s;
   * this is the piece of it that is only true of this pane.
   */
  async function reloadPreview() {
    await reload();
    setFramed("waiting");
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(siteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access can be denied; the URL is selectable either way.
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col animate-settle">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          {/* Honest while the site is still coming up: the address exists and is
              copyable, but claiming it is live is exactly the lie the user
              caught us in when the frame showed an error page. */}
          <p className="text-xs uppercase tracking-[0.14em] text-done">
            {checking ? "Your site is going live" : "Your site is live"}
          </p>
          <a
            href={siteUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-1 block truncate font-mono text-sm text-accent underline decoration-accent-soft underline-offset-4 transition-colors hover:decoration-accent"
          >
            {siteUrl}
          </a>
          {/* The sentence the frame cannot say: this is not a preview of a
              site, it is a site.

              Held back while the readiness poll is still running, for the same
              reason the eyebrow above says "going live" rather than "live" —
              the address exists but promising it is reachable is the exact lie
              the user caught us in when the frame showed an error page. */}
          {!checking && (
            <p className="mt-1 text-xs text-ink-faint">
              {HANDOFF_COPY.hosted}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!checking && (
            <button
              type="button"
              onClick={() => void reloadPreview()}
              disabled={reloading}
              title="Reload the preview"
              className="flex items-center gap-1.5 rounded-full border border-rule px-4 py-1.5 text-xs transition-colors hover:border-accent hover:text-accent disabled:opacity-60"
            >
              <span aria-hidden className="text-[13px] leading-none">
                ↻
              </span>
              {reloading ? "Reloading" : "Reload"}
            </button>
          )}
          <button
            type="button"
            onClick={copy}
            className="rounded-full border border-rule px-4 py-1.5 text-xs transition-colors hover:border-accent hover:text-accent"
          >
            {copied ? "Copied" : "Copy link"}
          </button>
          <button
            type="button"
            onClick={onRestart}
            className="rounded-full bg-ink px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
          >
            Build another
          </button>
        </div>
      </div>

      <div className="relative mt-4 min-h-0 flex-1 overflow-hidden rounded-xl border border-rule bg-surface">
        {checking ? (
          <ComingOnline waitedMs={site.waitedMs} />
        ) : framed === "unavailable" ? (
          <div className="flex h-full flex-col items-center justify-center px-8 text-center">
            <p className="text-sm text-ink-soft">This site can’t be shown in a preview frame.</p>
            <a
              href={siteUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 rounded-full bg-accent px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-hover"
            >
              Open your site
            </a>
          </div>
        ) : (
          <iframe
            src={frameUrl(siteUrl, loadKey)}
            title="Preview of your site"
            ref={frameRef}
            // Generated by an agent from a user prompt: treated as untrusted
            // third-party content, and given no access back to Hatch. The grant
            // is computed rather than literal — see `frameSandbox`, which adds
            // `allow-same-origin` only for a site that is provably not us,
            // because without it a page that touches localStorage dies before
            // it mounts and previews as a white rectangle.
            sandbox={frameSandbox(siteUrl)}
            referrerPolicy="no-referrer"
            className="size-full border-0"
          />
        )}
      </div>

      {hasBackend && !checking && <BackendBand sessionId={sessionId} />}

      {!checking && framed !== "unavailable" && (
        // Always present, because a frame that stayed blank is indistinguishable
        // from one that rendered — see FRAME_TIMEOUT_MS. Without this the user
        // is left staring at an empty box with no stated way out.
        <p className="mt-2 shrink-0 text-center text-xs text-ink-faint">
          Preview not showing?{" "}
          <a
            href={siteUrl}
            target="_blank"
            rel="noreferrer"
            className="text-accent underline underline-offset-2"
          >
            Open your site in a new tab
          </a>
        </p>
      )}
    </div>
  );
}

/**
 * The other half of what was built.
 *
 * The frame above shows the site. Without this the Xano half is something the
 * user watched scroll past in the transcript rather than something they can
 * look at.
 *
 * Xano's colours rather than the accent, and that is the point: this is a
 * handoff to a different product, and one wearing Hatch's single accent reads
 * as one more Hatch control. Same tokens the hatch skin's band uses — the band
 * should look like Xano whichever skin is mounted, which is the whole reason
 * those tokens exist. See index.css.
 *
 * An ordinary anchor to a Hatch route. What sits behind it is a one-time token
 * with a 60-second life, so there is no URL to render here — see
 * api/src/xano/impersonate.ts.
 */
function BackendBand({ sessionId }: { sessionId: string }) {
  return (
    <a
      href={backendHandoffPath(sessionId)}
      target="_blank"
      rel="noreferrer"
      className="group mt-3 flex shrink-0 items-center gap-3 rounded-xl bg-xano-deep px-4 py-3 transition-colors hover:bg-xano"
    >
      <span
        aria-hidden
        className="grid size-7 shrink-0 place-items-center rounded-md bg-xano text-xs font-bold text-white group-hover:bg-white group-hover:text-xano-deep"
      >
        X
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-white">
          {HANDOFF_COPY.cta}
        </span>
        {/* Stated rather than discovered. The session really is read-only, and
            someone who tries to add a field and cannot must read that as the
            tour it is rather than as a product that is broken. */}
        <span className="block text-xs text-xano-tint">
          {HANDOFF_COPY.scope}
        </span>
      </span>
      <span aria-hidden className="shrink-0 text-sm text-white">
        &rarr;
      </span>
    </a>
  );
}

/**
 * Every non-preview ending. Honest message, and a way forward — never a link,
 * because on these paths there is nothing to link to.
 */
function Closed({
  canvas,
  onRestart,
}: {
  canvas: Extract<CanvasState, { state: "closed" }>;
  onRestart: () => void;
}) {
  const succeeded = canvas.outcome === "succeeded";
  const heading = succeeded
    ? "This build finished without a site"
    : canvas.outcome === "rejected"
      ? "All builders are busy"
      : canvas.outcome === "expired"
        ? "This build ran out of time"
        : canvas.outcome === "cancelled"
          ? "You stopped this build"
          : "This build did not finish";

  return (
    <div className="rounded-2xl border border-rule bg-surface px-8 py-14 text-center animate-settle">
      <p className={`text-xs uppercase tracking-[0.14em] ${succeeded ? "text-ink-faint" : "text-halt"}`}>
        {heading}
      </p>
      <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-ink-soft">
        {canvas.message ?? "Something went wrong on our side."}
      </p>
      <button
        type="button"
        onClick={onRestart}
        className="mt-8 rounded-full bg-accent px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-accent-hover"
      >
        Try again
      </button>
    </div>
  );
}
